import { RefObject, useCallback, useState } from 'react';
import useWatchTime from 'src/hooks/useWatchTime';
import ApplicationService from 'src/services/ApplicationService';
import LiveService from 'src/services/LiveService';
import { valueFromPercentage, calculatePercentage } from 'src/utils/mathUtils';
import { getMouseOrTouchPosition } from 'src/utils/browserUtils';
import { getHoursMinutesSecondsFrom } from 'src/utils/playerUtils';
import useApplicationData from 'src/hooks/useApplicationData';
import { TRANSMISSION_TYPE } from 'src/interfaces/enums';
import useTranslation from 'next-translate/useTranslation';
import { SEEKBAR_PERCENT_INIT } from '../constants';

export type SeekbarEvent = MouseEvent | TouchEvent;
export type ReactSeekbarEvent = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>;
interface IUseSeekbarProps {
  currentPercent: number;
  onSeeking: (percentValue: number) => void;
  onSeekInit?: () => void;
  onSeekEnded?: () => void;
  seekBarElementRef: RefObject<HTMLElement>;
}

const MAX_POSITION = 100;

export default function useSeekbar({
  currentPercent,
  onSeeking,
  onSeekInit,
  onSeekEnded,
  seekBarElementRef,
}: IUseSeekbarProps) {
  const [temporaryPercent, setTemporaryPercent] = useState<number>(currentPercent);
  const [seeking, setSeeking] = useState<boolean>(false);
  const [targetTime, setTargetTime] = useState(0);
  const [timePreviewPosition, setTimePreviewPosition] = useState(0);

  const { duration, currentTime } = useWatchTime();
  const { transmissionType } = useApplicationData();
  const { t } = useTranslation('common');
  const isLive = transmissionType === TRANSMISSION_TYPE.live;

  const getSeekbarDimensions = useCallback(() => {
    if (!seekBarElementRef.current) {
      return {
        width: 1,
        height: 0,
        top: 0,
        left: 0,
      };
    }
    return seekBarElementRef.current.getBoundingClientRect();
  }, [seekBarElementRef]);

  const getSeekbarPositionFrom = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const { left, top, width, height } = getSeekbarDimensions();
      const eventPosition = getMouseOrTouchPosition(event);

      if (width > height) return eventPosition.x - left;
      return eventPosition.y - top;
    },
    [getSeekbarDimensions],
  );

  const calcSeekbarPercent = useCallback(
    (event: SeekbarEvent) => {
      const { width, height } = getSeekbarDimensions();

      const seekbarPercent = calculatePercentage(getSeekbarPositionFrom(event), Math.max(width, height));

      return Math.max(seekbarPercent, SEEKBAR_PERCENT_INIT);
    },
    [getSeekbarDimensions, getSeekbarPositionFrom],
  );

  const seek = useCallback(
    function (event: SeekbarEvent) {
      const newPosition = calcSeekbarPercent(event);
      setTemporaryPercent(calcSeekbarPercent(event));

      onSeeking(newPosition <= MAX_POSITION ? newPosition : MAX_POSITION);
    },
    [calcSeekbarPercent, onSeeking],
  );

  const endSeeking = useCallback(
    function (event: SeekbarEvent) {
      setSeeking(false);

      onSeekEnded?.();

      if (event instanceof MouseEvent) {
        document.removeEventListener('mousemove', seek);
        document.removeEventListener('mouseup', endSeeking);
      } else {
        document.removeEventListener('touchmove', seek);
        document.removeEventListener('touchend', endSeeking);
      }
    },
    [onSeekEnded, seek],
  );

  const startSeeking = useCallback(
    function (event: ReactSeekbarEvent) {
      onSeekInit?.();

      seek(event.nativeEvent);
      setSeeking(true);

      if (event.nativeEvent instanceof MouseEvent) {
        document.addEventListener('mousemove', seek);
        document.addEventListener('mouseup', endSeeking);
      } else {
        document.addEventListener('touchmove', seek);
        document.addEventListener('touchend', endSeeking);
      }
    },
    [endSeeking, onSeekInit, seek],
  );

  const updateVodTimeTarget = useCallback(
    (event: ReactSeekbarEvent) => {
      const targetTime = valueFromPercentage(calcSeekbarPercent(event.nativeEvent), duration);

      setTimePreviewPosition(getSeekbarPositionFrom(event.nativeEvent));
      setTargetTime(targetTime);
    },
    [calcSeekbarPercent, duration, getSeekbarPositionFrom],
  );

  const updateLiveTimeTarget = useCallback(
    (event: ReactSeekbarEvent) => {
      const liveCurrentTime = (ApplicationService.player as LiveService)?.liveTracker?.liveCurrentTime || 0;
      const targetTime = valueFromPercentage(calcSeekbarPercent(event.nativeEvent), liveCurrentTime);

      setTimePreviewPosition(getSeekbarPositionFrom(event.nativeEvent));
      setTargetTime(targetTime);
    },
    [calcSeekbarPercent, getSeekbarPositionFrom],
  );

  const updateTimeTargetPreview = useCallback(
    (event: ReactSeekbarEvent) => {
      if (isLive) {
        updateLiveTimeTarget(event);
      } else {
        updateVodTimeTarget(event);
      }
    },
    [isLive, updateLiveTimeTarget, updateVodTimeTarget],
  );

  const generateTimeLabelFrom = useCallback(
    (time: number) => {
      const { hours, minutes, seconds } = getHoursMinutesSecondsFrom(time);

      const timeValues = {
        minutes_value: minutes,
        seconds_value: seconds,
      };

      const timeLabel =
        hours <= 0
          ? t('aria_labels.controls.seekbar.without_hours', timeValues)
          : t('aria_labels.controls.seekbar.with_hours', {
              ...timeValues,
              hours_value: hours,
            });

      return timeLabel;
    },
    [t],
  );

  const generateAriaValueText = useCallback(() => {
    const currentTimeLabel = generateTimeLabelFrom(currentTime);
    const durationTimeLabel = generateTimeLabelFrom(duration);

    return `${currentTimeLabel} ${t('aria_labels.controls.seekbar.divider')} ${durationTimeLabel}`;
  }, [currentTime, duration, generateTimeLabelFrom, t]);

  return {
    startSeeking,
    positionPercent: seeking ? temporaryPercent : currentPercent,
    updateTimeTargetPreview,
    targetTime,
    timePreviewPosition,
    seeking,
    generateAriaValueText,
    duration,
    currentTime,
  };
}
